import axios from 'axios';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ClipboardList, Upload } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { usePermissions } from '@/lib/PermissionsContext';
import { GoogleMapsBtn } from '@/components/GoogleMapsBtn';
import { API_URL } from './utils';

export function SectionGestionContrat({ bon }) {
  const { user } = useAuth();
  const { canDoBonTravailAction } = usePermissions() || {};
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [sousCtiteres, setSousCriteres] = useState([]);
  const [evaluations, setEvaluations] = useState({});
  const [notesGlobales, setNotesGlobales] = useState('');
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Permission de remplir (peut modifier) vs juste voir (spectateur)
  const canRemplir = canDoBonTravailAction ? canDoBonTravailAction('bt_gestion_contrat_remplir') : false;
  const canVoir = canDoBonTravailAction ? canDoBonTravailAction('bt_gestion_contrat_voir') : false;
  // Fallback basé sur le rôle si le contexte de permissions n'est pas prêt (fail-open volontaire — la matrice bt_gestion_contrat_* couvre déjà les 3 rôles ci-dessous).
  // Cat H — dispatch UI (isCP = affiche le bouton "Remplir/Visualiser"). Le vrai lockdown est fait via `readOnly = !canRemplir`.
  const isCP = canRemplir || ['charge_projet', 'adjoint_admin', 'proprietaire'].includes(user?.role);
  const readOnly = !canRemplir;

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/performance/gestion-contrat/${bon.id}`);
      const data = res.data;
      setSousCriteres(data.sous_criteres || []);
      if (data.form) {
        setForm(data.form);
        setEvaluations(Object.fromEntries(
          Object.entries(data.form.sous_criteres || {}).map(([k, v]) => [k, v.passed !== undefined ? v.passed : true])
        ));
        setNotesGlobales(data.form.notes_generales || '');
        setPhotos(data.form.photos || []);
      } else {
        setForm(null);
        const initial = {};
        (data.sous_criteres || []).forEach(sc => { initial[sc.id] = true; });
        setEvaluations(initial);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, [bon.id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        rempli_par: `${user?.prenom || ''} ${user?.nom || ''}`.trim() || 'CP',
        sous_criteres: Object.fromEntries(
          sousCtiteres.map(sc => [sc.id, { passed: evaluations[sc.id] !== false, label: sc.label, scope: sc.scope }])
        ),
        notes_generales: notesGlobales,
      };
      const res = await axios.post(`${API_URL}/api/performance/gestion-contrat/${bon.id}`, payload);
      const data = res.data;
      if (res.status === 200) {
        toast.success('Formulaire Gestion du Contrat sauvegardé');
        setForm(data.form);
        setOpen(false);
        load();
      } else {
        toast.error(data.detail || 'Erreur sauvegarde');
      }
    } catch (e) {
      toast.error('Erreur réseau');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotos = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      const { compressImageList } = await import('@/lib/imageCompression');
      const compressed = await compressImageList(files);
      const formData = new FormData();
      compressed.forEach(f => formData.append('files', f));
      const res = await axios.post(`${API_URL}/api/performance/gestion-contrat/${bon.id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const data = res.data;
      if (res.status === 200) {
        setPhotos(prev => [...prev, ...data.photos]);
        toast.success(`${data.photos.length} photo(s) ajoutée(s)`);
      }
    } catch (e) {
      toast.error('Erreur upload photos');
    } finally {
      setUploading(false);
    }
  };

  const chefCriteres = sousCtiteres.filter(sc => sc.scope === 'chef');
  const allCriteres = sousCtiteres.filter(sc => sc.scope === 'all');

  const scoreChef = chefCriteres.length > 0 ? Math.round(chefCriteres.filter(sc => evaluations[sc.id] !== false).length / chefCriteres.length * 100) : 100;
  const scoreAll = allCriteres.length > 0 ? Math.round(allCriteres.filter(sc => evaluations[sc.id] !== false).length / allCriteres.length * 100) : 100;

  return (
    <Card className="mt-6">
      <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle className="flex items-center flex-wrap gap-2 text-base">
          <ClipboardList className="w-5 h-5 text-blue-600 shrink-0" />
          <span>Gestion du Contrat</span>
          {form ? (
            <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-xs">Rempli</Badge>
          ) : (
            <Badge variant="outline" className="text-xs text-green-600 border-green-300">Réussi par défaut</Badge>
          )}
        </CardTitle>
        {(isCP || canVoir) && (
          <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="self-start sm:self-auto">
            <ClipboardList className="w-4 h-4 mr-1" />
            {readOnly ? 'Visualiser' : (form ? 'Modifier' : 'Remplir')}
          </Button>
        )}
      </CardHeader>

      {form && (
        <CardContent>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="text-xs text-slate-500 mb-1">Score Chef d'équipe</div>
              <div className={`text-xl font-bold ${form.chef_score_pct >= 80 ? 'text-emerald-600' : 'text-red-500'}`}>
                {form.chef_score_pct?.toFixed(0)}%
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="text-xs text-slate-500 mb-1">Score Tout l'équipe</div>
              <div className={`text-xl font-bold ${form.all_score_pct >= 80 ? 'text-emerald-600' : 'text-red-500'}`}>
                {form.all_score_pct?.toFixed(0)}%
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-2">Rempli par : {form.rempli_par} · {form.date_evaluation?.slice(0, 10)}</p>
          {photos.length > 0 && (
            <div className="mt-3 flex gap-2 flex-wrap">
              {photos.map((p, i) => (
                <a key={p.id || p._id || i} href={`${API_URL}${p}`} target="_blank" rel="noreferrer">
                  <img src={`${API_URL}${p}`} alt="" className="w-16 h-16 object-cover rounded border" />
                </a>
              ))}
            </div>
          )}
        </CardContent>
      )}

      {/* Dialog formulaire */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-blue-600" />
              Formulaire Gestion du Contrat
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            {/* Score live */}
            <div className="grid grid-cols-2 gap-3">
              <div className={`rounded-lg p-3 text-center ${scoreChef >= 80 ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                <div className={`text-2xl font-bold ${scoreChef >= 80 ? 'text-emerald-600' : 'text-red-500'}`}>{scoreChef}%</div>
                <div className="text-xs text-slate-500">Chef d'équipe</div>
              </div>
              <div className={`rounded-lg p-3 text-center ${scoreAll >= 80 ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                <div className={`text-2xl font-bold ${scoreAll >= 80 ? 'text-emerald-600' : 'text-red-500'}`}>{scoreAll}%</div>
                <div className="text-xs text-slate-500">Tout l'équipe</div>
              </div>
            </div>

            {/* Critères Chef uniquement */}
            {chefCriteres.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1">
                  <Badge className="text-xs bg-purple-100 text-purple-700">Chef d'équipe uniquement</Badge>
                  {readOnly && <Badge className="text-xs bg-slate-100 text-slate-600">Lecture seule</Badge>}
                </h4>
                <div className="space-y-2">
                  {chefCriteres.map(sc => (
                    <div key={sc.id} className={`p-3 rounded-lg border ${evaluations[sc.id] !== false ? 'bg-white border-slate-200' : 'bg-red-50 border-red-200'}`}>
                      <label className={`flex items-center gap-3 ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}>
                        <Checkbox
                          checked={evaluations[sc.id] !== false}
                          disabled={readOnly}
                          onCheckedChange={v => !readOnly && setEvaluations(prev => ({ ...prev, [sc.id]: v }))}
                        />
                        <span className="text-sm text-slate-700 flex-1">{sc.label}</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Critères Tout le monde */}
            {allCriteres.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1">
                  <Badge className="text-xs bg-green-100 text-green-700">Tout l'équipe</Badge>
                </h4>
                <div className="space-y-2">
                  {allCriteres.map(sc => (
                    <label key={sc.id} className={`flex items-center gap-3 p-3 rounded-lg border ${readOnly ? 'cursor-default' : 'cursor-pointer'} ${evaluations[sc.id] !== false ? 'bg-white border-slate-200' : 'bg-red-50 border-red-200'}`}>
                      <Checkbox
                        checked={evaluations[sc.id] !== false}
                        disabled={readOnly}
                        onCheckedChange={v => !readOnly && setEvaluations(prev => ({ ...prev, [sc.id]: v }))}
                      />
                      <span className="text-sm text-slate-700">{sc.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Notes globales */}
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Notes générales</label>
              <Textarea
                value={notesGlobales}
                onChange={e => setNotesGlobales(e.target.value)}
                placeholder={readOnly ? 'Aucune note' : "Contexte, observations particulières..."}
                rows={3}
                disabled={readOnly}
              />
            </div>

            {/* Upload photos */}
            {!readOnly && (
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">Photos (général)</label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={e => handlePhotos(Array.from(e.target.files))}
                  className="hidden"
                  id="gc-photos"
                />
                <label htmlFor="gc-photos" className="cursor-pointer">
                  <Upload className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                  <span className="text-sm text-slate-500">Cliquez pour ajouter des photos</span>
                </label>
                {uploading && <p className="text-xs text-blue-500 mt-1">Upload en cours...</p>}
              </div>
              {photos.length > 0 && (
                <div className="mt-2 flex gap-2 flex-wrap">
                  {photos.map((p, i) => (
                    <img key={p.id || p._id || i} src={`${API_URL}${p}`} alt="" className="w-14 h-14 object-cover rounded border" />
                  ))}
                </div>
              )}
            </div>
            )}
            {readOnly && photos.length > 0 && (
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-2">Photos</label>
                <div className="flex gap-2 flex-wrap">
                  {photos.map((p, i) => (
                    <img key={p.id || p._id || i} src={`${API_URL}${p}`} alt="" className="w-14 h-14 object-cover rounded border" />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => setOpen(false)}>Fermer</Button>
            {!readOnly && (
              <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


/* ─── Dialog Édition Bon de Travail ─── */
